import type { GoalsByMetric } from '../goalsByMetric';

export type SafeTOrgKey = 'skyway' | 'droneport';

export interface QuarterPoint {
  /** Display label like "Q1 2025" */
  quarter: string;
  qoq?: Record<string, unknown>;
  yoy?: Record<string, unknown>;
}

export interface ClientAcquisitionPoint extends QuarterPoint {
  engaged: number;
  acquired: number;
  internationalTestingAcquired?: number;
}

export interface RevenuePoint extends QuarterPoint {
  revenueNewClients: number;
  revenueAllClients: number;
}

export interface TulsaPoint extends QuarterPoint {
  totalRevenue: number;
  tulsaRevenue: number;
  totalProducers: number;
  tulsaProducers: number;
}

export interface RiskPoint extends QuarterPoint {
  appliedLow: number;
  appliedMedium: number;
  appliedHigh: number;
}

export interface PoamPoint extends QuarterPoint {
  unmitigatedNoPoam: number;
  poamOpenNotDeveloped: number;
  poamDevelopedNotMonitored: number;
  poamMonitoredNotShared: number;
  poamShared: number;
}

export interface SafeTOrgSeries {
  key: SafeTOrgKey;
  displayName: string;
  clientAcquisition: ClientAcquisitionPoint[];
  revenue: RevenuePoint[];
  tulsa: TulsaPoint[];
  /** All-time Tulsa impact totals (includes periods not shown). */
  tulsaAllTime: {
    totalRevenue: number;
    tulsaRevenue: number;
    totalProducers: number;
    tulsaProducers: number;
  };
  goalsByMetric: GoalsByMetric;
}

export interface SafeTCombinedSeries {
  clientAcquisition: ClientAcquisitionPoint[];
  revenue: RevenuePoint[];
  tulsa: TulsaPoint[];
  /** All-time Tulsa impact totals (includes periods not shown). */
  tulsaAllTime: {
    totalRevenue: number;
    tulsaRevenue: number;
    totalProducers: number;
    tulsaProducers: number;
  };
  byOrgLatest: {
    skyway: {
      engaged: number;
      acquired: number;
      revenueAllClients: number;
      tulsaRevenue: number;
      totalRevenue: number;
    };
    droneport: {
      engaged: number;
      acquired: number;
      revenueAllClients: number;
      tulsaRevenue: number;
      totalRevenue: number;
    };
  };
}

export interface SafeTSecuritySeries {
  risk: RiskPoint[];
  poams: PoamPoint[];
}

export interface SafeTDashboardData {
  skyway: SafeTOrgSeries;
  droneport: SafeTOrgSeries;
  combined: SafeTCombinedSeries;
  tuSecurity: SafeTSecuritySeries;
}

function pctChange(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

export function formatPct(n: number | null, decimals: number = 0): string {
  if (n === null || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(decimals)}%`;
}

export function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatCompactMoney(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

export function formatSignedCurrency(n: number): string {
  const sign = n > 0 ? '+' : '';
  return `${sign}${formatCurrency(Math.abs(n))}`;
}

export function latest<T>(arr: T[]): T {
  return arr[arr.length - 1];
}

export function previous<T>(arr: T[]): T | null {
  return arr.length >= 2 ? arr[arr.length - 2] : null;
}

export function getClientConversion(acquired: number, engaged: number): number | null {
  if (!Number.isFinite(acquired) || !Number.isFinite(engaged) || engaged === 0) return null;
  return acquired / engaged;
}

export function getClientConversionQoQ(series: ClientAcquisitionPoint[]): number | null {
  if (!series?.length) return null;
  const cur = latest(series);
  const prev = previous(series);
  if (!prev) return null;
  const curConv = getClientConversion(cur.acquired, cur.engaged);
  const prevConv = getClientConversion(prev.acquired, prev.engaged);
  if (curConv === null || prevConv === null || prevConv === 0) return null;
  return pctChange(curConv, prevConv);
}

export function getTulsaShare(tulsaRevenue: number, totalRevenue: number): number | null {
  if (!Number.isFinite(tulsaRevenue) || !Number.isFinite(totalRevenue) || totalRevenue === 0) return null;
  return tulsaRevenue / totalRevenue;
}

export function getTulsaShareQoQ(series: TulsaPoint[]): number | null {
  if (!series?.length) return null;
  const cur = latest(series);
  const prev = previous(series);
  if (!prev) return null;
  const curShare = getTulsaShare(cur.tulsaRevenue, cur.totalRevenue);
  const prevShare = getTulsaShare(prev.tulsaRevenue, prev.totalRevenue);
  if (curShare === null || prevShare === null || prevShare === 0) return null;
  return pctChange(curShare, prevShare);
}

export function getNewRevenueShare(revenueNewClients: number, revenueAllClients: number): number | null {
  if (!Number.isFinite(revenueNewClients) || !Number.isFinite(revenueAllClients) || revenueAllClients === 0) return null;
  return revenueNewClients / revenueAllClients;
}

export function getNewRevenueShareQoQ(series: RevenuePoint[]): number | null {
  if (!series?.length) return null;
  const cur = latest(series);
  const prev = previous(series);
  if (!prev) return null;
  const curShare = getNewRevenueShare(cur.revenueNewClients, cur.revenueAllClients);
  const prevShare = getNewRevenueShare(prev.revenueNewClients, prev.revenueAllClients);
  if (curShare === null || prevShare === null || prevShare === 0) return null;
  return pctChange(curShare, prevShare);
}

export function getMediumHighRiskShare(point: RiskPoint): number | null {
  const total = point.appliedLow + point.appliedMedium + point.appliedHigh;
  if (!Number.isFinite(total) || total === 0) return null;
  return (point.appliedMedium + point.appliedHigh) / total;
}
